import { PurchasePointsEarningInvitation } from "@/features/purchasePoints/components/Balance/PurchasePointsEarningInvitation";
import { PurchasePointsValidityNotice } from "@/features/purchasePoints/components/Balance/PurchasePointsValidityNotice";
import { BALANCE_FIGURES_ROW, BALANCE_FIGURES_ROW_CENTERED, BalanceFigure } from "@/features/purchasePoints/components/BalanceFigure";
import { PurchasePointsConvertCta } from "@/features/purchasePoints/components/PurchasePointsConvertCta";
import { PurchasePointsErrorState } from "@/features/purchasePoints/components/PurchasePointsErrorState";
import {
  PURCHASE_POINTS_AVAILABLE_LABEL,
  PURCHASE_POINTS_BALANCE_ERROR_MESSAGE,
  PURCHASE_POINTS_CONVERTED_LABEL,
  PURCHASE_POINTS_EXPIRED_LABEL,
  PURCHASE_POINTS_PENDING_EXPLAINER,
  PURCHASE_POINTS_PENDING_LABEL,
  PURCHASE_POINTS_REWARDS_PER_PAGE,
} from "@/features/purchasePoints/config/purchasePoints.config";
import { isAccessCode } from "@/features/purchasePoints/errors/purchasePointsError";
import { usePurchasePointsBalance } from "@/features/purchasePoints/hooks/usePurchasePointsBalance";
import { usePurchasePointsRewards } from "@/features/purchasePoints/hooks/usePurchasePointsRewards";
import { hasNeverEarned } from "@/features/purchasePoints/utils/readSummaryState";

// Its own first page, the very one `RewardHistoryList` reads: sharing the key
// spares a second request for data already in flight.
const FIRST_PAGE = 1;

const CARD = "rounded-xl border bg-white p-5 shadow-sm dark:bg-gray-800";
const ACTIVE_CARD = `${CARD} border-gray-200 dark:border-gray-700`;
const DORMANT_CARD = `${CARD} border-dashed border-gray-300 dark:border-gray-600`;

const DormantCard = () => (
  <div className={DORMANT_CARD} data-testid="purchase-points-balance-dormant">
    <div className={`${BALANCE_FIGURES_ROW} opacity-60`}>
      <BalanceFigure
        label={PURCHASE_POINTS_AVAILABLE_LABEL}
        points={null}
        testId="purchase-points-available-placeholder"
        isPrimary
      />
      <BalanceFigure
        label={PURCHASE_POINTS_PENDING_LABEL}
        points={null}
        testId="purchase-points-pending-placeholder"
      />
      <BalanceFigure
        label={PURCHASE_POINTS_EXPIRED_LABEL}
        points={null}
        testId="purchase-points-expired-placeholder"
      />
      <BalanceFigure
        label={PURCHASE_POINTS_CONVERTED_LABEL}
        points={null}
        testId="purchase-points-converted-placeholder"
      />
    </div>

    <PurchasePointsEarningInvitation />
  </div>
);

export const PurchasePointsBalancePanel = () => {
  const {
    balance, isLoading: isBalanceLoading, isDisabled, error, retry,
  } = usePurchasePointsBalance();
  const { rewards, isLoading: isRewardsLoading, error: rewardsError } = usePurchasePointsRewards(FIRST_PAGE, PURCHASE_POINTS_REWARDS_PER_PAGE);

  // No employee attached to the session: nothing was requested, so there is
  // neither a failure nor a balance to announce.
  if (isDisabled) return null;

  if (isBalanceLoading || isRewardsLoading) {
    return (
      <div
        role="status"
        aria-busy="true"
        aria-label={PURCHASE_POINTS_AVAILABLE_LABEL}
        data-testid="purchase-points-balance-skeleton"
        className="h-28 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700"
      />
    );
  }

  if (error) {
    return (
      <div
        className={ACTIVE_CARD}
        data-testid="purchase-points-balance-error"
        // An access refusal already announces itself politely; only a failure
        // has to interrupt, and the skeleton it replaces takes its live region
        // away with it.
        role={isAccessCode(error.code) ? undefined : "alert"}
      >
        <PurchasePointsErrorState
          error={error}
          onRetry={retry}
          fallbackMessage={PURCHASE_POINTS_BALANCE_ERROR_MESSAGE}
        />
      </div>
    );
  }

  if (!balance) return null;

  // A rewards failure proves nothing about whether gains exist: it must never
  // be read as dormant, the state a technical outage cannot decide.
  if (!rewardsError && hasNeverEarned(balance, rewards)) return <DormantCard />;

  return (
    <div className={ACTIVE_CARD} data-testid="purchase-points-balance">
      {/* Not in the dormant card: an employee with no yeaties earned yet has
          no points for the rule to bear on. */}
      <PurchasePointsValidityNotice />

      <div className={BALANCE_FIGURES_ROW}>
        <div className={BALANCE_FIGURES_ROW_CENTERED}>
          <BalanceFigure
            label={PURCHASE_POINTS_AVAILABLE_LABEL}
            points={balance.available_points}
            testId="purchase-points-available"
            isPrimary
          />

          <PurchasePointsConvertCta
            availablePoints={balance.available_points}
            pointsPerEuro={balance.points_per_euro}
          />
        </div>

        <BalanceFigure
          label={PURCHASE_POINTS_PENDING_LABEL}
          points={balance.pending_points}
          testId="purchase-points-pending"
          explainer={PURCHASE_POINTS_PENDING_EXPLAINER}
        />
        <BalanceFigure
          label={PURCHASE_POINTS_EXPIRED_LABEL}
          points={balance.expired_points}
          testId="purchase-points-expired"
        />
        {/* Without it, the available figure would simply drop after a
            conversion and read as points gone missing. */}
        <BalanceFigure
          label={PURCHASE_POINTS_CONVERTED_LABEL}
          points={balance.converted_points}
          testId="purchase-points-converted"
        />
      </div>
    </div>
  );
};

PurchasePointsBalancePanel.displayName = "PurchasePointsBalancePanel";
